<template>
    <div :class="['product-item', { dark: theme == THEMES.DARK, selected }]" v-if="item">
        <label v-if="selectable" class="selection-control" @click.stop>
            <input
                type="checkbox"
                :checked="selected"
                :aria-label="`Select ${getDisplayName(item)}`"
                @change="$emit('toggle-selection', item)"
            />
        </label>
        <div class="product-image">
            <img :src="getServerAssetUrl(item.image)" alt="Product image" />
        </div>
        <div class="product-info">
            <div class="product-name">
                <router-link :to="`/products/${item.parent ? item.parent : item.id}`">
                    {{ getDisplayName(item) }}
                </router-link>
            </div>
            <div :class="['status-chip', `status-${resolvedStatus.toLowerCase().replace(/\s+/g, '-')}`]">
                {{ resolvedStatus }}
            </div>
            <div class="product-details">
                <div v-if="item.stock" class="stock-info">
                    <i class="material-icons">inventory_2</i>
                    <span>In Stock: {{ item.stock }}</span>
                </div>
                <div v-else class="product-description">
                    {{ item.short_description }}
                </div>
            </div>
        </div>
        <div class="product-actions">
            <button 
                class="action-btn" 
                title="View product details"
                @click="navigateToView"
            >
                <i class="material-icons">visibility</i>
            </button>
        </div>
    </div>
</template>

<script>
import { getServerAssetUrl } from "@/utils/common"
import { mapState } from "vuex"

export default {
    props: {
        item: {
            type: Object,
            default: null,
        },
        selectable: { type: Boolean, default: false },
        selected: { type: Boolean, default: false },
    },
    methods: {
        getServerAssetUrl,
        navigateToView() {
            this.$router.push(`/products/${this.item.parent ? this.item.parent : this.item.id}`);
        },
        getDisplayName(item) {
            // Use server-provided display_name when available
            if (item.display_name && item.display_name.trim()) {
                return item.display_name;
            }
            
            // Fallback to client-side composition
            const segments = [];
            
            // Add brand if present
            if (item.brand && item.brand.trim()) {
                segments.push(item.brand.trim());
            }
            
            // Add item name (always include if present)
            if (item.item_name && item.item_name.trim()) {
                let itemNameSegment = item.item_name.trim();
                
                // Add strain to item name if present
                if (item.strain && item.strain.trim()) {
                    itemNameSegment += ` ${item.strain.trim()}`;
                }
                
                segments.push(itemNameSegment);
            }
            
            // Add weight if present (prefer net_weight + weight_uom, fallback to weight string)
            let weightSegment = '';
            if (item.net_weight && item.weight_uom) {
                weightSegment = `${item.net_weight}${item.weight_uom}`;
            } else if (item.weight && item.weight.trim()) {
                weightSegment = item.weight.trim();
            }
            
            if (weightSegment) {
                segments.push(weightSegment);
            }
            
            // Join segments with " - " separator, or fallback to original name
            return segments.length > 0 ? segments.join(' - ') : (item.item_name || item.name || 'Product');
        }
    },
    computed: {
        ...mapState("misc", ["theme", "THEMES"]),
        resolvedStatus() {
            if (this.item && this.item.product_status) return this.item.product_status
            return this.item && this.item.disabled ? "Inactive" : "Active"
        },
    },
}
</script>

<style lang="scss" scoped>
@use "sass:color";
@import "../../assets/variables.scss";

.product-item {
    display: flex;
    align-items: center;
    border: 1px solid $lightBorderColor;
    padding: 10px;
    border-radius: 8px;
    background-color: $lightCardBg;
    transition: all 0.2s ease;
    width: 100%;
    height: 100%;
    box-sizing: border-box;

    &.selected {
        border-color: $primaryColor;
        box-shadow: inset 3px 0 0 $primaryColor;
    }
    
    &:hover {
        border-color: $primaryColor;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }
    
    .selection-control {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        min-width: 28px;
        margin: 0 8px 0 0;

        input {
            width: 17px;
            height: 17px;
            margin: 0;
            accent-color: $primaryColor;
            cursor: pointer;
        }
    }

    .product-image {
        width: 42px;
        height: 42px;
        margin-right: 12px;
        border-radius: 6px;
        overflow: hidden;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: $lightHoverColor;
        
        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }
    
    .product-info {
        flex: 1;
        min-width: 0;
        
        .product-name {
            font-weight: 600;
            font-size: 14px;
            margin-bottom: 3px;
            
            a {
                color: $textColor;
                text-decoration: none;
                
                &:hover {
                    color: $primaryColor;
                }
            }
        }

        .status-chip {
            display: inline-flex;
            align-items: center;
            min-height: 20px;
            margin-bottom: 3px;
            padding: 2px 7px;
            border: 1px solid rgba($textColor, 0.16);
            border-radius: 10px;
            background: rgba($textColor, 0.04);
            color: rgba($textColor, 0.74);
            font-size: 10px;
            font-weight: 600;
            line-height: 1;

            &.status-active { border-color: rgba($successColor, 0.35); color: color.adjust($successColor, $lightness: -10%); }
            &.status-coming-soon { border-color: rgba($infoColor, 0.4); color: color.adjust($infoColor, $lightness: -10%); }
            &.status-discontinued,
            &.status-archived { border-color: rgba($dangerColor, 0.3); color: color.adjust($dangerColor, $lightness: -8%); }
        }
        
        .product-details {
            font-size: 12px;
            color: rgba($textColor, 0.7);
            
            .stock-info {
                display: flex;
                align-items: center;
                
                i {
                    font-size: 13px;
                    margin-right: 3px;
                }
            }
        }
    }
    
    .product-actions {
        display: flex;
        gap: 8px;
        margin-left: 16px;
        
        .action-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            border-radius: 6px;
            background-color: transparent;
            border: none;
            cursor: pointer;
            color: rgba($textColor, 0.6);
            transition: all 0.2s ease;
            
            i {
                font-size: 18px;
            }
            
            &:hover {
                background-color: $lightHoverColor;
                color: $primaryColor;
            }
        }
    }
    
    &.dark {
        background-color: $darkCardBg;
        border-color: $darkBorderColor;
        
        &:hover {
            border-color: $secondaryColorDark;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        &.selected {
            border-color: $secondaryColorDark;
            box-shadow: inset 3px 0 0 $secondaryColorDark;
        }

        .selection-control input { accent-color: $secondaryColorDark; }
        
        .product-image {
            background-color: rgba(255, 255, 255, 0.05);
        }
        
        .product-info {
            .product-name {
                a {
                    color: $darkTextColor;
                    
                    &:hover {
                        color: $secondaryColorDark;
                    }
                }
            }
            
            .product-details {
                color: rgba($darkTextColor, 0.7);
            }

            .status-chip {
                border-color: rgba($darkTextColor, 0.22);
                background: rgba($darkTextColor, 0.06);
                color: rgba($darkTextColor, 0.8);

                &.status-active { border-color: rgba($successColorDark, 0.5); color: $successColorDark; }
                &.status-coming-soon { border-color: rgba($infoColorDark, 0.5); color: $infoColorDark; }
                &.status-discontinued,
                &.status-archived { border-color: rgba($dangerColorDark, 0.45); color: $dangerColorDark; }
            }
        }
        
        .product-actions {
            .action-btn {
                color: rgba($darkTextColor, 0.6);
                
                &:hover {
                    background-color: rgba(255, 255, 255, 0.1);
                    color: $secondaryColorDark;
                }
            }
        }
    }
}

@media (max-width: 576px) {
    .product-item {
        .product-actions {
            display: none;
        }
    }
}
</style>
